import {
  dayJsToMidnightUTCDateTimeString,
  getUserVisibleIndexValue,
  isValidArrayInnerType,
} from '@doss/common-all'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { useFormikContext } from 'formik'
import { isNil } from 'lodash'
import { HTMLInputTypeAttribute } from 'react'
import { cn } from '@/lib/utils'
import { InputWrapper } from '~/@/components/inputs/base/input-wrapper'
import { BaseInputWrapperProps } from '~/@/components/inputs/base/types'
import { DatePicker } from '~/@/components/inputs/date/date-picker/date-picker'
import { NumberFormatInput } from '~/@/components/inputs/number/number-format-input'
import { CheckboxGroup } from '~/@/components/inputs/selection/checkbox-group'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import { Checkbox } from '~/@/components/inputs/standalone/checkbox'
import { LongTextInput } from '~/@/components/inputs/text/long-text-input'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { DataType, IndexType, TableColumn } from '~/common/generated/apollo-operations'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { LookupCell } from '~/components/records/lookup/LookupCell'
import { buildEnumDropdownOptions } from '~/components/shared/enum-options'
import {
  RecordCellEditOrigin,
  useEditableTableCellOriginContext,
} from '~/components/table-cells/useEditableTableCellOriginProvider'
import { TableCellLoadingValue } from '../../table-cells/TableCellLoadingValue'
import { CellValue } from '../add-record/AddRecordDialog'
import { useRecordDetailsCellPermissions } from '../RecordDetailsCellPermissionsProvider'
import { AddRecordFormCell, RecordDetailTableCell, RecordDetailTableRow } from '../types'
import { ArrayCellDetails } from './ArrayCellDetails/ArrayCellDetails'
import { FileCellDetails } from './FileCellDetails'
import { RecordFormulaCell } from './RecordFormulaCell'
import { UserCellDetails } from './UserCellDetails'

dayjs.extend(utc)

export interface RecordCellDetailsProps {
  cell: RecordDetailTableCell | AddRecordFormCell
  showDirtyIndicator: boolean
  lookupGroupId?: string
  autofocus?: boolean
  addDocumentId?: (documentId: string) => void
  removeDocumentId?: (documentId: string) => void
  onCancel?: () => void
  columnWidth?: number
  parentRow?: Omit<RecordDetailTableRow, 'table' | 'rootWorkspace'>
  columns: Pick<TableColumn, 'id' | 'type' | 'innerType' | 'indexType'>[]
  tableId: string
  selectedLookupRowIds: string[]
  row?: Pick<RecordDetailTableRow, 'cells'>
  inputWrapperProps?: BaseInputWrapperProps
  showSaveButtonOnSide?: boolean
}

/**
 * Used to display the record details, which is a specific row in the table.
 */
export function RecordCellDetails({
  cell,
  showDirtyIndicator,
  lookupGroupId,
  autofocus,
  addDocumentId,
  removeDocumentId,
  onCancel,
  columnWidth,
  parentRow,
  columns,
  tableId,
  selectedLookupRowIds,
  row,
  inputWrapperProps: parentInputWrapperProps,
  showSaveButtonOnSide,
}: RecordCellDetailsProps) {
  const { locale, currency } = useGlobalContext()
  const formik = useFormikContext<Record<string, CellValue>>()
  const { isMobile } = useDeviceType()
  const { isReadOnly } = useRecordDetailsCellPermissions()
  const origin = useEditableTableCellOriginContext()

  const fieldName = cell.column.id
  const enableAutofocus = autofocus ?? false
  const fieldLabel = cell.column.name

  const columnFormat =
    'formatPreset' in cell.column
      ? {
          formatPreset: cell.column.formatPreset,
          formatOptions: cell.column.formatOptions,
        }
      : { formatPreset: undefined, formatOptions: undefined }

  const formFieldProps = {
    name: fieldName,
    value: formik.values[fieldName],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
  }

  // For lookup columns, show errors regardless of touched state
  const isLookupColumn =
    cell.column.type === DataType.LOOKUP ||
    (cell.column.type === DataType.ARRAY && cell.column.innerType === DataType.LOOKUP)
  const isTableLookupEdit = isLookupColumn && origin === RecordCellEditOrigin.TABLE_DETAILS

  const shouldCheckError = isLookupColumn || formik.touched[fieldName]
  const showError = shouldCheckError && Boolean(formik.errors[fieldName])

  const inputWrapperProps: BaseInputWrapperProps = {
    ...parentInputWrapperProps,
    errorText: showError && !isTableLookupEdit ? (formik.errors[fieldName] as string) : undefined,
  }

  let element: JSX.Element
  switch (cell.column.type) {
    case DataType.ARRAY: {
      if (
        (isNil(formFieldProps.value) || Array.isArray(formFieldProps.value)) &&
        isValidArrayInnerType(cell.column.innerType)
      ) {
        element = (
          <ArrayCellDetails
            addDocumentId={addDocumentId}
            cell={cell}
            columnWidth={columnWidth}
            columns={columns}
            enableAutofocus={enableAutofocus}
            fieldName={fieldName}
            inputWrapperProps={inputWrapperProps}
            onCancel={onCancel}
            origin={origin}
            parentRow={parentRow}
            removeDocumentId={removeDocumentId}
            row={row}
            selectedLookupRowIds={selectedLookupRowIds}
            showDirtyIndicator={showDirtyIndicator}
            value={formFieldProps.value ? formFieldProps.value : []}
          />
        )
        break
      }
      throw new Error('Invalid value or inner type for ARRAY column')
    }
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME: {
      // Formulas are not shown for Add Record so we don't need to display a value if there is no id
      element =
        'id' in cell ? (
          <RecordFormulaCell cell={cell} inputWrapperProps={inputWrapperProps} tableId={tableId} />
        ) : (
          <InputWrapper {...inputWrapperProps}>
            <TableCellLoadingValue />
          </InputWrapper>
        )
      break
    }
    // FORMULA_BOOLEAN is intentionally out of scope for the non-numeric-formulas epic (see
    // isFormula()'s doc comment in @doss/common-all's table-cell.ts) -- no cell-data schema or
    // resolver exists for it yet.
    case DataType.FORMULA_BOOLEAN:
      return null
    case DataType.BOOLEAN: {
      let fieldValue = false
      if (typeof formFieldProps.value === 'boolean') {
        fieldValue = formFieldProps.value
      } else if (typeof formFieldProps.value === 'string') {
        fieldValue = formFieldProps.value === 'true'
      }

      if (origin === RecordCellEditOrigin.TABLE_DETAILS) {
        // Center the checkbox if component is in the context of the editable table cell form
        element = (
          <div className="flex justify-center">
            <Checkbox
              checked={fieldValue}
              disabled={isReadOnly}
              onCheckedChange={(checked) => {
                formik.setFieldTouched(fieldName, true)
                formik.setFieldValue(fieldName, checked)
              }}
              {...inputWrapperProps}
            />
          </div>
        )
      } else {
        element = (
          <CheckboxGroup
            disabled={isReadOnly}
            options={[
              {
                value: fieldName,
                checked: fieldValue,
                onCheckedChange: (checked) => {
                  formik.setFieldTouched(fieldName, true)
                  formik.setFieldValue(fieldName, checked)
                },
                disabled: isReadOnly,
              },
            ]}
            {...inputWrapperProps}
          />
        )
      }
      break
    }
    case DataType.DATE: {
      let dateString: string | undefined
      if (formFieldProps.value && typeof formFieldProps.value === 'string') {
        dateString = formFieldProps.value
      }
      const date = dateString ? dayjs.utc(dateString) : undefined
      element = (
        <DatePicker
          disabled={isReadOnly}
          {...inputWrapperProps}
          {...columnFormat}
          onChange={(newDate) => {
            formik.setFieldTouched(fieldName, true)
            if (isNil(newDate)) {
              formik.setFieldValue(fieldName, null)
              return
            }

            // For DATE types, set to midnight UTC
            if (dayjs.isDayjs(newDate)) {
              formik.setFieldValue(
                fieldName,
                newDate.isValid() ? dayJsToMidnightUTCDateTimeString(newDate) : null,
              )
            }
          }}
          placeholder={fieldLabel}
          showClearButton={cell.column.isNullable}
          useModal={isMobile}
          value={date}
        />
      )

      break
    }
    case DataType.DATE_TIME: {
      let dateString: string | undefined
      if (formFieldProps.value && typeof formFieldProps.value === 'string') {
        dateString = formFieldProps.value
      }

      const date = dateString ? dayjs(dateString) : undefined
      element = (
        <DatePicker
          disabled={isReadOnly}
          {...inputWrapperProps}
          {...columnFormat}
          onChange={(newDate) => {
            formik.setFieldTouched(fieldName, true)
            if (isNil(newDate)) {
              formik.setFieldValue(fieldName, null)
              return
            }

            if (dayjs.isDayjs(newDate)) {
              formik.setFieldValue(fieldName, newDate.isValid() ? newDate.toISOString() : null)
            }
          }}
          placeholder={fieldLabel}
          showClearButton={cell.column.isNullable}
          useModal={isMobile}
          value={date}
          withTime
        />
      )

      break
    }
    case DataType.ENUM: {
      // Mui prints warning to console if provide empty string since empty string does not match
      // any of the enum values
      let enumValue: string | undefined = undefined
      if (typeof formFieldProps.value === 'string' && formFieldProps.value.length > 0) {
        enumValue = formFieldProps.value
      }

      element = (
        <Dropdown
          disabled={isReadOnly}
          {...inputWrapperProps}
          onChange={(value) => {
            formik.setFieldTouched(fieldName, true)
            formik.setFieldValue(fieldName, value ?? null)
          }}
          options={buildEnumDropdownOptions(cell.column.enumSet?.options)}
          placeholder={fieldLabel}
          showClearButton={cell.column.isNullable}
          value={enumValue}
        />
      )
      break
    }
    case DataType.FILE_URL: {
      let documentId
      if (cell.documents.length === 0) {
        documentId = null
      } else {
        documentId = cell.documents[0].id
      }
      element = (
        <FileCellDetails
          addDocumentId={addDocumentId}
          cell={cell}
          documentId={documentId}
          inputWrapperProps={inputWrapperProps}
          removeDocumentId={removeDocumentId}
        />
      )
      break
    }
    case DataType.USER: {
      element = (
        <UserCellDetails
          fieldName={fieldName}
          inputWrapperProps={inputWrapperProps}
          value={formFieldProps.value}
        />
      )

      break
    }
    case DataType.ID: {
      let textFieldType: HTMLInputTypeAttribute | undefined
      switch (cell.column.indexType) {
        case IndexType.NONE:
          throw new Error(`ID column ${cell.column.id} has no index type`)
        case IndexType.NUMBER:
          textFieldType = 'number'
          break
        case IndexType.STRING:
          textFieldType = 'text'
          break
      }

      if (cell.column.indexAutogenerate) {
        if (typeof formFieldProps.value !== 'number' && typeof formFieldProps.value !== 'string') {
          return <div className="ps-1 pt-1">Auto-generated</div>
        }
        const value = getUserVisibleIndexValue(
          formFieldProps.value,
          cell.column.indexPrefix,
          cell.column.indexSuffix,
        )
        element = <ShortTextInput disabled {...inputWrapperProps} value={value} />
      } else {
        const startAdornment = cell.column.indexPrefix ? (
          <div className="-me-2">{cell.column.indexPrefix}</div>
        ) : null
        const endAdornment = cell.column.indexSuffix ? <div>{cell.column.indexSuffix}</div> : null
        element = (
          <ShortTextInput
            autoFocus={enableAutofocus}
            disabled={isReadOnly}
            endAdornment={endAdornment}
            {...inputWrapperProps}
            onChange={(event) => {
              let value: string | null = event.target.value
              if (value === '') {
                value = null
              }
              formik.setFieldTouched(fieldName, true)
              formik.setFieldValue(fieldName, value)
            }}
            placeholder={fieldLabel}
            startAdornment={startAdornment}
            type={textFieldType}
            value={formFieldProps.value ? (formFieldProps.value as string) : ''}
          />
        )
      }

      break
    }
    case DataType.LOOKUP: {
      element = (
        <InputWrapper {...inputWrapperProps}>
          <LookupCell
            cell={cell}
            columnWidth={columnWidth}
            columns={columns}
            lookupGroupId={lookupGroupId}
            onCancel={onCancel}
            parentRow={parentRow}
            row={row}
            selectedLookupRowIds={selectedLookupRowIds}
            showDirtyIndicator={showDirtyIndicator}
          />
        </InputWrapper>
      )
      break
    }
    case DataType.NUMBER: {
      const rawValue = formFieldProps.value
      let numericValue: number | null = null
      if (typeof rawValue === 'number') {
        numericValue = rawValue
      } else if (typeof rawValue === 'string' && rawValue.trim() !== '') {
        const parsed = Number(rawValue)
        numericValue = Number.isNaN(parsed) ? null : parsed
      }

      element = (
        <NumberFormatInput
          {...inputWrapperProps}
          autoFocus={enableAutofocus}
          defaultCurrency={currency}
          disabled={isReadOnly}
          formatOptions={columnFormat.formatOptions}
          formatPreset={columnFormat.formatPreset}
          locale={locale}
          name={fieldName}
          onBlur={() => formik.setFieldTouched(fieldName, true)}
          onChange={(nextValue) => {
            formik.setFieldTouched(fieldName, true)
            formik.setFieldValue(fieldName, nextValue)
          }}
          placeholder={fieldLabel}
          value={numericValue}
        />
      )
      break
    }
    case DataType.STRING: {
      const value = formFieldProps.value ?? ''
      element = (
        <LongTextInput
          autoFocus={enableAutofocus}
          disabled={isReadOnly}
          {...inputWrapperProps}
          name={fieldName}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          onFocus={(e) => {
            const target = e.target
            target.setSelectionRange(target.value.length, target.value.length)
          }}
          placeholder={fieldLabel}
          value={value as string}
        />
      )
      break
    }
  }

  const additionalDetails: JSX.Element = <></>

  // For lookup cells in the table details, don't add padding since there is already padding in
  // the expanded lookup autocomplete dropdown
  const nonLookup =
    cell.column.type !== DataType.LOOKUP && cell.column.innerType !== DataType.LOOKUP
  const isTableEdit = origin === RecordCellEditOrigin.TABLE_DETAILS

  // For multiple file displays, move the dirty status indicator to the top
  const isArrayOfFiles =
    isTableEdit &&
    cell.column.type === DataType.ARRAY &&
    cell.column.innerType === DataType.FILE_URL

  const isMobileLookupCell = !nonLookup && isMobile

  return (
    <div
      className={cn(
        'flex items-center w-full',
        isArrayOfFiles && 'items-start',
        isMobileLookupCell && 'h-full overflow-hidden',
      )}
    >
      {additionalDetails}
      <div
        className={cn(
          'flex-1 w-full',
          nonLookup && isTableEdit && 'pt-2 px-2 pb-0',
          showSaveButtonOnSide && 'pb-2 pe-1',
          isMobileLookupCell && 'h-full overflow-hidden',
        )}
      >
        <div className="w-full">{element}</div>
      </div>
    </div>
  )
}
